import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { api, ApiError } from "@/lib/api";
import { setToken } from "@/lib/auth";
import { GlasstapeWordmark } from "@/components/ds";
import styles from "./auth-callback.module.sass";

const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || "";

// Auth0 SDK errors that only a visible redirect can clear. `/login` is a manual
// gate (it needs a click), so sending the user there cannot spin into a loop.
const NEEDS_REDIRECT = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "missing_refresh_token",
  "invalid_grant",
]);

// The callback is a step in the sign-in flow, not an app screen: three of its
// four outcomes are waiting states, not failures. Only `fault` is an error.
type Outcome =
  | { kind: "working" }
  | { kind: "verify"; message: string }
  | { kind: "pending"; message: string }
  | { kind: "fault"; title: string; message: string };

const MailGlyph = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="5" width="19" height="14" rx="1.5" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const ClockGlyph = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.2l3.4 2" />
  </svg>
);

const AlertGlyph = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3.6 21.2 20H2.8L12 3.6Z" />
    <path d="M12 10v4.4M12 17.4h.01" />
  </svg>
);

export default function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isLoading, isAuthenticated, user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [outcome, setOutcome] = useState<Outcome>({ kind: "working" });
  const [retrying, setRetrying] = useState(false);

  const authError = params.get("error");
  const authErrorDescription = params.get("error_description");

  // NEEDS_REDIRECT errors go to /login, never to a silent retry: silent auth would
  // need third-party cookies (no refresh tokens) and cannot show a consent prompt.
  const exchange = useCallback(
    async () => {
      try {
        const accessToken = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH0_AUDIENCE },
        });
        const data = await api.exchangeAuth0Token(accessToken);
        setToken(data.access_token, data.email);
        const returnTo = sessionStorage.getItem("auth_return_to") || "/";
        sessionStorage.removeItem("auth_return_to");
        navigate(returnTo, { replace: true });
      } catch (err: unknown) {
        const e = err as { error?: string };
        if (e?.error && NEEDS_REDIRECT.has(e.error)) {
          navigate("/login", { replace: true });
          return;
        }
        const code = err instanceof ApiError ? err.code : undefined;
        const message = err instanceof Error ? err.message : "Sign-in error.";
        if (code === "email_not_verified") setOutcome({ kind: "verify", message });
        else if (code === "no_role") setOutcome({ kind: "pending", message });
        else setOutcome({ kind: "fault", title: "Sign-in error", message });
      }
    },
    [getAccessTokenSilently, navigate],
  );

  useEffect(() => {
    if (authError) {
      setOutcome({
        kind: "fault",
        title: "Authentication error",
        message: authErrorDescription || authError,
      });
      return;
    }
    if (isLoading || !isAuthenticated) return;
    if (outcome.kind !== "working") return;
    void exchange();
  }, [authError, authErrorDescription, isLoading, isAuthenticated, outcome.kind, exchange]);

  // The awaited claim is stamped when Auth0 issues a token, so picking up a freshly
  // verified address needs a new token; the existing session avoids re-entering credentials.
  const retry = (fresh: boolean) => {
    setRetrying(true);
    void loginWithRedirect({
      appState: { returnTo: sessionStorage.getItem("auth_return_to") || "/" },
      authorizationParams: fresh ? { prompt: "login" } : {},
    });
  };

  if (outcome.kind === "working") {
    return (
      <div className={styles.pageCenter}>
        <p className={styles.loadingText}>Signing in...</p>
      </div>
    );
  }

  const isFault = outcome.kind === "fault";

  return (
    <div className={styles.page}>
      <div className={styles.stack}>
        <div className={styles.brand}>
          <GlasstapeWordmark size={26} />
        </div>

        <div className={styles.panel}>
          <span className={isFault ? styles.glyphFault : styles.glyph}>
            {outcome.kind === "verify" ? <MailGlyph /> : outcome.kind === "pending" ? <ClockGlyph /> : <AlertGlyph />}
          </span>

          {outcome.kind === "verify" && (
            <>
              <h1 className={styles.title}>Check your inbox.</h1>
              <p className={styles.sub}>{outcome.message}</p>
              {user?.email && <p className={styles.address}>{user.email}</p>}
              <div className={styles.actions}>
                <button type="button" className={styles.primaryBtn} onClick={() => retry(false)} disabled={retrying}>
                  {retrying ? "Redirecting..." : "I've verified my email"}
                </button>
                <button
                  type="button"
                  className={styles.ghostBtn}
                  onClick={() => retry(true)}
                  disabled={retrying}
                >
                  Use another account
                </button>
              </div>
              <p className={styles.hint}>
                It might take a few minutes to arrive.
              </p>
            </>
          )}

          {outcome.kind === "pending" && (
            <>
              <h1 className={styles.title}>Your account is not active yet.</h1>
              <p className={styles.sub}>{outcome.message}</p>
              {user?.email && <p className={styles.address}>{user.email}</p>}
              <div className={styles.actions}>
                <a href="/login" className={styles.primaryBtn}>Back to sign in</a>
              </div>
            </>
          )}

          {isFault && (
            <>
              <h1 className={styles.title}>{outcome.title}</h1>
              <p className={styles.sub}>{outcome.message}</p>
              <div className={styles.actions}>
                <a href="/login" className={styles.primaryBtn}>Back to sign in</a>
              </div>
            </>
          )}
        </div>

        <p className={styles.meta}>Secure sign-in</p>
      </div>
    </div>
  );
}
